class MarqueeController extends HTMLElement {
  constructor() {
    super();
    this.position = 0;
    this.speed = 50;
    this.direction = 'ltr';
    this.isStatic = false;
    this._running = false;
    this.box = null;
    this.innerElement = null;
    this.boxWidth = 0;
    this.innerElementWidth = 0;
    this.intersectionObserver = null;
    this.resizeTimeout = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
    this.stop();
  }

  init() {
    this.checkStaticLayout();

    if (!this.isStatic) {
      this.initMarquee();
      this.initHoverEvents();
      this.initObserver();
    }

    this.handleResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.onResize(), 150);
    };
    window.addEventListener('resize', this.handleResize);

    this.classList.add('initialized');
  }

  checkStaticLayout() {
    const enableStatic = this.dataset.enableStatic === 'true';

    if (!enableStatic) {
      this.isStatic = false;
      this.removeAttribute('data-static');
      return;
    }

    const innerBlock = this.querySelector('.marquee-controller__block');
    if (!innerBlock) {
      this.isStatic = false;
      return;
    }

    const items = innerBlock.querySelectorAll('.marquee-controller__block__item:not(span)');
    const isDesktop = window.innerWidth >= 768;

    this.isStatic = isDesktop && items.length < 4;

    if (this.isStatic) {
      this.setAttribute('data-static', 'true');
    } else {
      this.removeAttribute('data-static');
    }
  }

  initMarquee() {
    this.box = this;
    const innerElement = this.children && this.children[0];

    if (!innerElement) {
      console.error('No child node found in marquee-controller');
      return;
    }

    innerElement.style.position = 'relative';
    this.innerElement = innerElement.cloneNode(true);
    this.speed = window.innerWidth > 768 ? parseInt(this.dataset.scrollingSpeed || 50) : parseInt(this.dataset.scrollingSpeed || 50) / 1.5;
    this.direction = this.dataset.scrollingDirection || 'ltr';

    this.refreshWidths();
    this.setUpChildren();
  }

  refreshWidths() {
    const rect = this.getBoundingClientRect();
    this.boxWidth = rect.right - rect.left;

    if (this.children[0]) {
      const childRect = this.children[0].getBoundingClientRect();
      this.innerElementWidth = childRect.right - childRect.left;
    }
  }

  calculateNumElements() {
    return Math.ceil(this.boxWidth / this.innerElementWidth) + 1;
  }

  setUpChildren() {
    const qty = this.calculateNumElements();

    if (qty > this.children.length) {
      for (let i = this.children.length; i < qty; i++) {
        this.appendChild(this.innerElement.cloneNode(true));
      }
    } else if (qty < this.children.length) {
      for (let i = qty; i < this.children.length; i++) {
        this.removeChild(this.lastChild);
      }
    }
  }

  nextFrame(delta) {
    this.refreshWidths();
    this.setUpChildren();

    Array.from(this.children).forEach((el) => {
      if (this.direction === 'rtl') {
        el.style.transform = `translateX(${this.position}px)`;
      } else {
        el.style.transform = `translateX(${-this.position}px)`;
      }
    });

    this.position += (this.speed * delta) / 1000;

    if (this.position >= this.innerElementWidth) {
      this.position = this.position % this.innerElementWidth;
    }
  }

  start() {
    this._running = true;
    let lastTime = null;

    const loop = () => {
      if (!this._running) return;

      const now = Date.now();
      const delta = lastTime === null ? 0 : now - lastTime;
      this.nextFrame(delta);
      lastTime = now;

      window.requestAnimationFrame(loop);
    };

    window.requestAnimationFrame(loop);
  }

  stop() {
    this._running = false;
  }

  initHoverEvents() {
    if (this.dataset.pauseOnHover !== 'true') return;

    let windowInFocus = true;

    window.addEventListener('blur', () => {
      windowInFocus = false;
    });

    window.addEventListener('focus', () => {
      windowInFocus = true;
    });

    this.addEventListener('mouseover', () => {
      if (windowInFocus) {
        this.stop();
      }
    });

    this.addEventListener('mouseout', () => {
      if (windowInFocus) {
        this.start();
      }
    });
  }

  initObserver() {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.start();
      } else {
        this.stop();
      }
    });
    this.intersectionObserver.observe(this);
  }

  cleanupClonedElements() {
    const blocks = this.querySelectorAll('.marquee-controller__block');

    for (let i = 1; i < blocks.length; i++) {
      blocks[i].remove();
    }

    if (blocks[0]) {
      blocks[0].style.transform = '';
      blocks[0].style.position = '';

      const items = blocks[0].children;
      Array.from(items).forEach((item) => {
        item.style.transform = '';
        item.style.position = '';
      });
    }
  }

  onResize() {
    const wasStatic = this.isStatic;
    this.checkStaticLayout();

    if (wasStatic !== this.isStatic) {
      this.stop();

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (this.isStatic) {
        this.cleanupClonedElements();
      } else {
        this.initMarquee();
        this.initObserver();
      }
    }
  }
}

if (!customElements.get('marquee-controller')) {
  customElements.define('marquee-controller', MarqueeController);
}
